import { computed, inject, Injectable, signal } from '@angular/core';
import { AppError } from '@/app/core/http/app-error.model';
import { toAppError } from '@/app/core/http/error-catalog';
import { DEFAULT_PAGE_SIZE, PageMeta } from '@/app/core/models/paged-result.model';
import {
  StaffChangeBatch,
  StaffChangeHistoryFilters,
  StaffChangeHistoryRecord,
  StaffChangeProcess,
  StaffChangeSortBy,
  StaffChangeSortOrder,
  StaffChangeUniverse,
} from '../models/staff-change-history.model';
import { StaffChangeHistoryRepository } from '../services/staff-change-history/staff-change-history.repository';

export type StaffChangeHistoryStatus = 'loading' | 'ready' | 'empty' | 'error';

export type StaffChangeBatchStatus = 'idle' | 'loading' | 'ready' | 'error';

const emptyPage = (size: number): PageMeta => ({
  page: 0,
  size,
  totalElements: 0,
  totalPages: 1,
});

const DEFAULT_SORT: { field: StaffChangeSortBy; order: StaffChangeSortOrder } = {
  field: 'fecha',
  order: 'desc',
};

const ERROR_SUBJECT = 'el historial de cambios de staff';
const ERROR_SUBJECT_BATCH = 'el detalle del cambio';

@Injectable()
export class StaffChangeHistoryStore {
  private readonly repository = inject(StaffChangeHistoryRepository);

  private readonly _status = signal<StaffChangeHistoryStatus>('loading');
  private readonly _records = signal<readonly StaffChangeHistoryRecord[]>([]);
  private readonly _pageMeta = signal<PageMeta>(emptyPage(DEFAULT_PAGE_SIZE));
  private readonly _filters = signal<StaffChangeHistoryFilters>({});
  private readonly _sort = signal(DEFAULT_SORT);
  private readonly _availableOperationTypes = signal<readonly StaffChangeProcess[]>([]);
  private readonly _availableStaffTypes = signal<readonly StaffChangeUniverse[]>([]);
  private readonly _error = signal<AppError | null>(null);

  private readonly _batchStatus = signal<StaffChangeBatchStatus>('idle');
  private readonly _batch = signal<StaffChangeBatch | null>(null);
  private latestBatchRequest = 0;

  readonly status = this._status.asReadonly();
  readonly error = this._error.asReadonly();
  readonly records = this._records.asReadonly();
  readonly pageMeta = this._pageMeta.asReadonly();
  readonly filters = this._filters.asReadonly();
  readonly sort = this._sort.asReadonly();
  readonly availableOperationTypes = this._availableOperationTypes.asReadonly();
  readonly availableStaffTypes = this._availableStaffTypes.asReadonly();

  readonly batch = this._batch.asReadonly();
  readonly batchStatus = this._batchStatus.asReadonly();
  readonly isBatchLoading = computed(() => this._batchStatus() === 'loading');

  readonly isLoading = computed(() => this._status() === 'loading');
  readonly resultCountLabel = computed(() => `${this._pageMeta().totalElements} cambio(s)`);

  search(filters: StaffChangeHistoryFilters): void {
    this._filters.set(filters);
    this.load(0);
  }

  sortBy(field: StaffChangeSortBy, order: StaffChangeSortOrder): void {
    this._sort.set({ field, order });
    this.load(0);
  }

  load(page = 0, size = this._pageMeta().size): void {
    this._error.set(null);
    this._status.set('loading');

    const { field, order } = this._sort();

    this.repository
      .getPage({ page, size, sortBy: field, sortOrder: order, ...this._filters() })
      .subscribe({
        next: (result) => {
          this._records.set(result.content);
          this._pageMeta.set(result.page);
          this._availableOperationTypes.set(result.availableOperationTypes);
          this._availableStaffTypes.set(result.availableStaffTypes);
          this._status.set(result.page.totalElements === 0 ? 'empty' : 'ready');
        },
        error: (cause: unknown) => {
          this._error.set(toAppError(cause, ERROR_SUBJECT));
          this._records.set([]);
          this._pageMeta.set(emptyPage(size));
          this._status.set('error');
        },
      });
  }

  openBatch(batchId: string): void {
    const request = ++this.latestBatchRequest;

    this._error.set(null);
    this._batch.set(null);
    this._batchStatus.set('loading');

    this.repository.getBatch(batchId).subscribe({
      next: (batch) => {
        if (request !== this.latestBatchRequest) return;
        this._batch.set(batch);
        this._batchStatus.set('ready');
      },
      error: (cause: unknown) => {
        if (request !== this.latestBatchRequest) return;
        this._error.set(toAppError(cause, ERROR_SUBJECT_BATCH));
        this._batchStatus.set('error');
      },
    });
  }

  closeBatch(): void {
    this.latestBatchRequest += 1;
    this._batch.set(null);
    this._batchStatus.set('idle');
  }
}
